import React from 'react';

import { CheckCircle } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import Button from '../../components/common/Button';
import AuthLayout from '../../components/layout/AuthLayout';
import PaymentSummary from '../../components/payment/PaymentSummary';
import { useInvoicePaymentSummary } from '../../hooks/useInvoiceApi';

// Standalone, unauthenticated page. Reached either straight after sign-up, via the "Pay Now" link
// in the welcome email (the invoice id itself is the auth, matching the emailed link staying valid
// indefinitely), or as Stripe's own success_url redirect (?paid=true) right after a successful
// Checkout - that query param is what tells "I just paid" apart from "revisiting an already-paid
// invoice", since both land on the same isPaid state otherwise.
const Pay: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [searchParams] = useSearchParams();
  const justPaid = searchParams.get('paid') === 'true';
  const {
    data: summary,
    isLoading,
    isError,
  } = useInvoicePaymentSummary(invoiceId);

  if (isLoading) {
    return (
      <AuthLayout title="Loading" description="Fetching your invoice...">
        <div />
      </AuthLayout>
    );
  }

  if (isError || !summary) {
    return (
      <AuthLayout
        title="Invoice Not Found"
        description="We couldn't find that invoice. Please check the link or contact support."
      >
        <div />
      </AuthLayout>
    );
  }

  if (summary.isPaid) {
    return (
      <AuthLayout
        title={justPaid ? 'Payment Successful' : 'Already Paid'}
        description={
          justPaid
            ? "You're all set - your GolfPlayed membership is now active."
            : 'This invoice was already paid, nothing more to do here.'
        }
      >
        <div className="mt-6 rounded-xl border border-green-200 bg-green-50 p-6 text-center">
          <CheckCircle className="mx-auto mb-3 h-10 w-10 text-green-600" />
          <p className="text-lg font-semibold text-green-800">
            {justPaid
              ? 'Thanks for joining GolfPlayed!'
              : 'This invoice has already been paid'}
          </p>
          {summary.invoiceNumber && (
            <p className="mt-1 text-sm text-green-700">
              Invoice {summary.invoiceNumber}
            </p>
          )}
        </div>

        <Link to="/login" className="mt-4 block">
          <Button className="w-full">Sign In to GolfPlayed</Button>
        </Link>
        <Link to="/explore" className="mt-3 block">
          <Button variant="secondary" className="w-full">
            Explore the Map
          </Button>
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Complete Your Payment"
      description="Thanks for joining GolfPlayed - complete your membership payment below."
    >
      <div className="mt-6">
        <PaymentSummary summary={summary} />
      </div>
    </AuthLayout>
  );
};

export default Pay;
